/** @jsx jsx */
import { colors } from '@apollo/brand';
import { css, jsx } from '@emotion/react';
import { DocumentNode } from 'graphql';
import * as monacoEditor from 'monaco-editor';
import React from 'react';
import { useRouteMatch } from 'react-router';

import { getEditorLineNumber } from 'src/app/graph/documentationPage/schemaPage/sdlEditorHelpers';
import { LineLinks } from 'src/app/graph/documentationPage/sdlPage/lineLinks/LineLinks';
import { useDerivedEditorValue } from 'src/app/graph/explorerPage/hooks/useDerivedEditorValue';
import { useEditorModel } from 'src/app/graph/explorerPage/hooks/useEditorModel';
import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { sandboxGraphRouteConfig } from 'src/app/graph/routes';
import { raw as IconErrorSolid } from 'src/assets/monaco-icon-error-solid.svg';
import {
  ThemeName,
  useThemeContext,
} from 'src/components/themeProvider/ThemeProvider';
import { useRouteParams } from 'src/hooks/useRouteParams';
import {
  MONACO_READONLY_OPTIONS,
  StudioEditorContainer,
} from 'src/hooks/useStudioEditor';

import { sdlDefinitionProviderFactory } from './sdlDefinitionProviderFactory';
import { sdlHoverProviderFactory } from './sdlHoverProviderFactory';
import { TypeMap } from './TypeMap';

export const LINK_ICON_EDITOR_CLASS = 'xxx-monaco-link-to-line-glyph';

export const SDL_EDITOR_MONACO_OPTIONS = {
  ...MONACO_READONLY_OPTIONS,
  lineNumbers: 'on',
  glyphMargin: true,
  hover: {
    delay: 0,
  },
} as const;

const { bg } = colors.tokens;

export const SDLEditor = ({
  className,
  editor,
  schemaName,
  documentNode,
  documentNodesByName,
  modelsByName,
  rangesToHide,
}: {
  className?: string;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  schemaName: string;
  documentNode: DocumentNode;
  documentNodesByName: Record<string, DocumentNode>;
  modelsByName: Record<string, monacoEditor.editor.ITextModel>;
  rangesToHide?: monacoEditor.Range[];
}) => {
  const monacoDisposables = React.useRef<monacoEditor.IDisposable[]>();

  // TODO: figure out how to make it implicit that editor side effects
  // update when the text changes,
  // so when the text first gets a value what we want to happen happens
  const editorTextValue = useDerivedEditorValue({
    editor,
    getValue: (x) => x,
    withPosition: false,
  });

  const { currentSdlRouteConfig } = useInternalGraphLinking();
  const {
    selectedSchema,
    graphqlTypeName,
    fieldName,
    argumentName,
    directiveName,
    typeName,
  } = useRouteParams(currentSdlRouteConfig);
  const graphTypeNode = graphqlTypeName || typeName;
  let lineNumberToScrollTo: number | undefined;
  // this might be slow, lets only do it when there are hash params in the url
  if (graphTypeNode || directiveName) {
    lineNumberToScrollTo = getEditorLineNumber(
      documentNode,
      graphTypeNode,
      fieldName,
      argumentName,
      directiveName,
    );
  }

  // Dispose of the completion item provider on dismount
  React.useEffect(
    () => () => {
      monacoDisposables.current?.forEach((disposable) => disposable.dispose());
    },
    [],
  );

  React.useEffect(() => {
    if (!rangesToHide) return;
    editor.setHiddenAreas(rangesToHide);
  }, [editor, editorTextValue, rangesToHide]);

  const allMarkers = React.useMemo(() => {
    const monacoDeltaDecorations: monacoEditor.editor.IModelDeltaDecoration[] =
      [];
    if (lineNumberToScrollTo) {
      monacoDeltaDecorations.push({
        range: new monacoEditor.Range(
          lineNumberToScrollTo,
          1,
          lineNumberToScrollTo,
          1,
        ),
        options: {
          isWholeLine: true,
          className: 'xxx-monaco-highlight-line',
          linesDecorationsClassName: 'xxx-monaco-highlight-line',
          marginClassName: 'xxx-monaco-highlight-line',
        },
      });
    }

    return {
      modelDecorations: [...(monacoDeltaDecorations ?? [])],
    };
  }, [lineNumberToScrollTo]);

  const model = useEditorModel(editor);

  React.useEffect(() => {
    if (!model) return;

    const decorations = editor.createDecorationsCollection(
      allMarkers.modelDecorations,
    );

    // Clean up decorations
    return () => {
      decorations.clear();
    };

    // this needs to happen on mount and when text is loaded,
    // so update whenever the editor text changes
  }, [editor, allMarkers, editorTextValue, lineNumberToScrollTo, model]);

  React.useEffect(() => {
    if (lineNumberToScrollTo) {
      editor.revealLineInCenterIfOutsideViewport(lineNumberToScrollTo);
    }

    // this needs to happen on mount and when text is loaded,
    // so update whenever the editor text changes
  }, [editor, editorTextValue, lineNumberToScrollTo]);

  const allSchemas = Object.fromEntries(
    Object.entries(modelsByName).map(([key, value]) => [
      key,
      { documentNode: documentNodesByName[key], model: value },
    ]),
  );

  const providerState = {
    schemaName,
    documentNode,
    allSchemas,
  };
  const providerStateRef = React.useRef(providerState);
  providerStateRef.current = providerState;

  React.useEffect(() => {
    editor.focus();

    // This will only be called upon mounting; so we'll have to use a
    // `ref` to hold the schema so that when this callback is called,
    // it's response can be changed after instantiation
    monacoDisposables.current = [
      monacoEditor.languages.registerHoverProvider(
        'graphql',
        sdlHoverProviderFactory(providerStateRef),
      ),
      monacoEditor.languages.registerDefinitionProvider(
        'graphql',
        sdlDefinitionProviderFactory(providerStateRef),
      ),
    ];
  }, [editor]);

  const isSandboxRoute =
    useRouteMatch(sandboxGraphRouteConfig.definition) !== null;

  const { themeName } = useThemeContext();

  return (
    <div
      className={className}
      css={css`
        .xxx-monaco-highlight-line {
          background-color: ${themeName === ThemeName.DARK
            ? bg.highlight.dark
            : bg.highlight.base};
          z-index: -1;
        }

        /* TODO (jason) fix this upstream */
        /* The monaco glyph hover css is broken, add some size/padding */
        .monaco-editor-hover
          .hover-row
          > div:not(.hover-contents):not(.actions) {
          font-size: 12px;
          padding-top: 4px;
          padding-bottom: 4px;
          padding-left: 8px;
          padding-right: 8px;
          font-family: 'var(--font-mono)';
        }
        .monaco-editor-hover .hover-row .hover-contents p {
          padding-top: 8px;
        }
        /* Add a red x beside line numbers */
        .xxx-monaco-syntax-error-glyph ~ .line-numbers {
          background-image: url(${IconErrorSolid});
          left: 0px !important;
          width: 100% !important;
          padding-right: 26px;
          background-repeat: no-repeat;
          background-size: 16px 16px;
          background-position: 7px 2px;
        }
        .xxx-monaco-line-added-glyph {
          background: green;
          margin-left: 10px;
          width: 10px !important;
        }
        .xxx-monaco-line-modified-glyph {
          background: blue;
          margin-left: 10px;
          width: 10px !important;
        }
      `}
    >
      <TypeMap
        typeDefs={documentNode}
        selectedSchema={selectedSchema}
        editor={editor}
      />
      <StudioEditorContainer editor={editor} className="h-full" />
      {!isSandboxRoute && (
        <LineLinks
          key={model?.id}
          editor={editor}
          typeDefs={documentNode}
          selectedSchema={selectedSchema}
        />
      )}
    </div>
  );
};
